import { defineQueuedModal } from '@heidi/runtime/shell';
import { ModalId } from '@heidi/schema/ml-scribe';

/**
 * The lazy pointer every descriptor carries — inert for this kind today.
 *
 * The launch queue reads a modal contribution for its ELIGIBILITY METADATA
 * only: `src/atoms/launchModalsConfigAtom.ts` copies `id` / `priority` /
 * `dismissible` / `showOnce` / `showOnlyAfterAccountCreation` into the same
 * `addModalToQueue` every legacy entry uses, and never touches `load`. So
 * `ScribeLayoutCsatPopover.tsx` keeps rendering from the mount
 * `GlobalModalsWrapper` already owns, and this flip is a declaration-home
 * change and nothing else.
 *
 * THE POPOVER HAS SINCE LANDED IN THIS PACKAGE AND THIS THUNK STAYS INERT,
 * which is the opposite of what this docblock used to predict. The surface takes
 * `isOpen`, `onClose`, a form-fields slot and the two emit seams as PROPS — the
 * queue registration and the shared CSAT fields are the app's — so no shell host
 * can render it from a `load` alone. `ScribeLayoutCsatPopoverHost` keeps the
 * mount, and only a descriptor whose surface needs nothing app-supplied can fill
 * this in.
 */
const rendersFromItsAppMount = () => Promise.resolve({ default: null });

/**
 * The only routes that render the V2 Scribe layout the survey asks about:
 * `/scribe/session/[...id]` and the split `/scribe/sessions/[[...id]]`
 * workspace (`src/container/session-page/v2`). SCR-7926 made this a positive
 * gate. Before it the predicate carried only the six-route deny list
 * transcribed from the config atom's `isValidPath` guard, so a clinician who
 * met the V2 tour a week earlier was asked how they found the refreshed
 * layout from Settings, Templates or a Work chat. Every route that deny list
 * named falls outside this prefix, so it is subsumed rather than kept.
 */
// Module-private on purpose: this door is star re-exported and aggregation
// rejects any non-descriptor export as `not-a-contribution` (C1).
const SCRIBE_LAYOUT_CSAT_ROUTES = ['/scribe/session', '/scribe/sessions'] as const;

/**
 * The session page, where the V2 layout is on screen. Matches each route
 * exactly or as a path segment (`/scribe/session/[...id]`), so a future
 * sibling such as `/scribe/session-templates` is not admitted by accident.
 */
const isScribeLayoutCsatRoute = (pathname: string): boolean =>
  SCRIBE_LAYOUT_CSAT_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );

/**
 * The scribe-layout CSAT popover — a star rating and a comment box shown to a
 * custom audience a week after they first met the V2 layout tour — declared by
 * the domain that owns it rather than by the app's central config atom.
 *
 * FROZEN IDENTITY. `id` keys backend `seen_modals` and `ModalView` dismiss
 * records, so it is the same enum member the legacy declaration carried: a
 * rename is a persisted-state migration, not a refactor, and it would resurface
 * the survey for every user who has already answered or dismissed it.
 *
 * `builds` is omitted, i.e. `cloud-only`, matching the first flip: the app
 * composes the registry as the `cloud` variant
 * (`src/composition/registry-atom.ts`) and no on-prem queue host exists to
 * aggregate the other arm.
 */
export const scribeLayoutCsatPopover = defineQueuedModal({
  id: ModalId.T5_CUSTOM_AUDIENCE_SCRIBE_LAYOUT_CSAT_POPOVER,
  /**
   * `2`, and this is the ONE line of the flip that is not behaviour-neutral.
   *
   * `ModalPriority.LOW` is 1 and `@heidi/dictate`'s `newInThisReleaseCard`
   * already holds it. `buildAppRegistry` requires globally unique priorities
   * across every contributed modal, so a second descriptor at `1` is rejected
   * as `[duplicate-priority]` and fails CI rather than shipping.
   *
   * No integer reproduces the pre-flip position, and that is a property of the
   * queue rather than a gap in the search. The queue sorts ONE merged list
   * descending by priority (`src/atoms/modalQueueAtoms.ts`), `Array#sort` is
   * stable, and a registry-sourced entry is always pushed AFTER the whole
   * legacy chain — so within a value the order is push order and a flipped
   * entry is last in its band. Measured at `87ac8785a9` the legacy `LOW` band
   * is 12 rows and this popover was 5th: it beat 7 and lost to 4. Any integer
   * above 1 (and below `NORMAL`'s 5) puts it above all eleven remaining rows;
   * `1` itself, or anything below, puts it under all eleven. "5th of 12" is not
   * reachable, and renumbering the dictation card does not create it either.
   * A fraction is refused outright: a user-triggered modal enqueued at the same
   * legacy level loses its tie to a launch modal by push order today
   * (`[...launchModals, ...userModals]`), and `0.99` silently flips that.
   *
   * So the programme owner ruled the delta rather than hiding it: `2`, which
   * hoists the popover above the four `LOW` rows that used to precede it —
   * `NINETY_DAY_TRIAL_ENDING_SOON_REMINDER_POPUP`, `MENU_COACHMARK_TOUR`,
   * `SCRIBE_LAYOUT_V2_TOUR`, `SCRIBE_LAYOUT_V2_INVITE` — and leaves its
   * relationship to the other seven, and to every other band, untouched. That
   * four-entry re-rank is the whole disclosed behaviour change, and
   * `src/atoms/modal-queue-growth-csat-flip.test.ts` pins both the pre-flip and
   * the post-flip order as literals so it can never widen unnoticed.
   *
   * This is the legacy `ModalPriority` NUMBER scale, NOT R1's frozen `× 10_000`
   * value. Unflipped entries are still on the 1–30 scale, so a frozen number
   * would not re-rank the popover, it would hoist it above every modal in the
   * app. The frozen scale becomes correct in the PR that removes the last
   * legacy entry (task Z3).
   */
  priority: 2,
  dismissible: true,
  showOnce: true,
  /**
   * Transcribed field for field from the deleted legacy gate, which read
   * `showScribeLayoutCsatPopover && hasToken && isValidPath && !isMobile &&
   * !isRecording`. Every input is a field `EligibilityContext` already carries,
   * which is the precondition for flipping at all — no side atom, no flag
   * getter, no app import.
   *
   * `showScribeLayoutCsatPopover` arrives PRE-RESOLVED and the R2 enterprise
   * suppress is already inside it: `isScribeLayoutCsatEligible` (this package's
   * `ui/scribe-layout-csat/eligibility.ts`, published on `.`) computes
   * `isLayoutEnabled && !isManaged && tourFirstSeenAt >= 1 week`, called app-side
   * in `GlobalModalsWrapper`. Do not re-implement that here, and in
   * particular do not reach for `context.isManaged` instead — the app-side
   * boolean also carries the layout flag and the one-week age, so substituting
   * the context field would widen the audience rather than restate the gate.
   *
   * `isRecording`, not `isClinicalRecordingActive` (R1): the legacy row read
   * the narrower signal, and a survey that stays suppressed through a PAUSED
   * consult would be a different gate.
   *
   * SCR-7926 added the last two conditions, both format fitness (T5):
   * - `isScribeLayoutCsatRoute`: ask about the refreshed layout only where it
   *   is on screen.
   * - `!isAskHeidiOpen`: the in-session Ask Heidi chat carries its own
   *   "How did Heidi do?" prompt, and this popover is anchored on the same
   *   bottom-end corner, so the two stacked. The popover defers rather than
   *   skips: `showOnce` marks it seen on close, not on withhold, so it returns
   *   once Ask Heidi closes.
   */
  eligible: (context) =>
    context.showScribeLayoutCsatPopover &&
    context.hasToken &&
    !context.isMobile &&
    !context.isRecording &&
    !context.isAskHeidiOpen &&
    isScribeLayoutCsatRoute(context.pathname),
  load: rendersFromItsAppMount,
});
